import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
// import { DividerOr } from '@/components/ui/divider-or';
// import { GoogleButton } from '@/components/ui/google-button';
import { Input } from '@/components/ui/input';
import { requestOtp } from '@shared/api/auth';
import { apiErrorMessage } from '@shared/api/client';
import { useCooldown } from '@shared/hooks/use-cooldown';
import { usePublicRedirect } from '@shared/hooks/use-me';
import { validateEmail } from '@shared/utils/validation';
import { AuthShell } from '@/features/auth/web/auth-shell';

// Associate-flavoured entry to the passwordless OTP flow. The intent travels
// on the URL (`/otp?as=associate`) so the OTP page can route a new email to
// /associate/onboarding reliably regardless of React state timing.
export function AssociateJoinPage() {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailTouched, setEmailTouched] = useState(false);
  const navigate = useNavigate();
  const { secondsLeft, active: onCooldown, start: startCooldown } = useCooldown(30);
  const { target, waiting } = usePublicRedirect();
  if (waiting) {
    return (
      <AuthShell>
        <div className="text-center text-sm text-gray-500">Loading…</div>
      </AuthShell>
    );
  }
  if (target) return <Navigate to={target} replace />;

  const request = useMutation({
    mutationFn: (e: string) => requestOtp(e, 'ASSOCIATE', true),
    onSuccess: () =>
      navigate(`/otp?email=${encodeURIComponent(email.trim())}&as=associate&signup=true`),
    onSettled: () => startCooldown(),
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setEmailTouched(true);
    const result = validateEmail(email);
    if (!result.isValid) {
      setEmailError(result.error);
      return;
    }
    setEmailError(null);
    if (onCooldown || request.isPending) return;
    request.mutate(email.trim());
  }

  function handleEmailChange(val: string) {
    setEmail(val);
    if (emailTouched) {
      const result = validateEmail(val);
      setEmailError(result.isValid ? null : result.error);
    }
  }

  function handleEmailBlur() {
    setEmailTouched(true);
    if (email.trim()) {
      const result = validateEmail(email);
      setEmailError(result.isValid ? null : result.error);
    }
  }

  return (
    <AuthShell>
      <div className="space-y-6">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-gray-900">Sign up as an Associate</h1>
          <p className="mt-1 text-sm text-gray-600">
            Serve registration &amp; compliance clients on EcRegi.
          </p>
        </div>

        <form className="space-y-4" onSubmit={onSubmit} noValidate>
          <div>
            <Input
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => handleEmailChange(e.target.value)}
              onBlur={handleEmailBlur}
              className={emailError ? 'border-red-500 focus:border-red-600 focus:ring-red-500/30' : undefined}
              required
              autoFocus
            />
            {emailError && (
              <p className="mt-1 text-xs font-medium text-red-600">{emailError}</p>
            )}
          </div>
          {request.isError ? (
            <p className="text-sm text-red-600">{apiErrorMessage(request.error)}</p>
          ) : null}
          <Button type="submit" block disabled={request.isPending || onCooldown}>
            {request.isPending
              ? 'Sending…'
              : onCooldown
                ? `Try again in ${secondsLeft}s`
                : 'Send OTP'}
          </Button>

          {/* <DividerOr /> */}

          {/* <GoogleButton /> */}

          <p className="pt-2 text-center text-xs text-gray-500">
            Already have an account?{' '}
            <a href="/associate/login" className="text-brand-700 underline underline-offset-2">
              Log in
            </a>
          </p>
        </form>
      </div>
    </AuthShell>
  );
}
